import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { HostedPlan, HostedPlanPublicationPreview, HostedPlanVersionInput } from '../types';
import { formatDate, formatQuotaBytes } from '../utils';
import { Icon } from './Icon';
import { AccordionItem } from './AccordionItem';

interface Props {
  plans: HostedPlan[];
  loading?: boolean;
  reload: () => Promise<void>;
  notify: (message: string, tone?: 'success' | 'error') => void;
  runWithRecentAuth: (label: string, execute: () => Promise<void>) => Promise<boolean>;
}

const FEATURE_LABELS: Record<string, string> = {
  'captions.manual': 'Manual captions',
  'captions.ai': 'AI subtitles',
  'captions.speaker_identification': 'Speaker identification beta',
  'captions.custom_colors': 'Custom caption colors',
  'embed.exact_path_rules': 'Page-level Embed Protection',
  'player.custom_colors': 'Custom player colors',
  'branding.white_label': 'White-label player',
};

const SECURITY_FEATURE_LABELS: Record<string, string> = {
  'security.dynamic_session_tokens': 'Dynamic session tokens',
  'security.hls_encryption': 'HLS encryption',
  'security.request_pacing': 'Request pacing',
  'security.forensic_session_marks': 'Forensic session marks',
};

const PUBLIC_BRANDING_FEATURE_LABELS: Record<string, string> = {
  'branding.public_page': 'Public page name, logo, and colors',
  'branding.player_logo': 'Member logo in embed player',
  'branding.custom_favicon': 'Member favicon and public page identity',
  'branding.hide_powered_by': 'Hide Powered by on public pages',
};

const PUBLIC_BRANDING_DEFAULTS: Record<string, boolean | number> = {
  'branding.public_page': false,
  'branding.player_logo': false,
  'branding.custom_favicon': false,
  'branding.hide_powered_by': false,
  'branding.custom_domain_limit': 0,
};

const FEATURE_DEFAULTS: Record<string, boolean | number | string> = {
  'captions.manual': true,
  'captions.ai': true,
  'captions.speaker_identification': false,
  'captions.custom_colors': false,
  'watermark.mode': 'standard_static',
  'embed.origin_limit': 1,
  'embed.exact_path_rules': false,
  'player.custom_colors': false,
  'branding.white_label': false,
  'security.dynamic_session_tokens': true,
  'security.hls_encryption': true,
  'security.request_pacing': true,
  'security.forensic_session_marks': true,
};

function message(reason: unknown): string {
  return reason instanceof ApiError ? reason.message : 'Unable to update the plan catalog.';
}

function rupiah(value: number): string {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);
}

function draftFor(plan: HostedPlan): HostedPlanVersionInput {
  return {
    title: plan.title,
    status: plan.status === 'draft' || plan.status === 'retired' ? plan.status : 'active',
    displayOrder: plan.displayOrder,
    monthlyPriceIdr: plan.offers.find((offer) => offer.billingInterval === 'monthly')?.priceIdr ?? 0,
    annualPriceIdr: plan.offers.find((offer) => offer.billingInterval === 'annual')?.priceIdr ?? 0,
    r2DeliveryQuotaBytes: plan.r2DeliveryQuotaBytes,
    protectedArchiveQuotaBytes: plan.protectedArchiveQuotaBytes,
    processingAllowanceSeconds: plan.processingAllowanceSeconds,
    subtitleAllowanceSeconds: plan.subtitleAllowanceSeconds,
    deletedVideoRecoveryDays: plan.deletedVideoRecoveryDays,
    sourceRecommendedBytes: plan.sourceRecommendedBytes,
    sourceMaxBytes: plan.sourceMaxBytes,
    sourceMaxDurationSeconds: plan.sourceMaxDurationSeconds,
    features: { ...FEATURE_DEFAULTS, ...plan.features },
    reason: `Owner published a new ${plan.title} plan version from the plan catalog.`,
    applyImprovementsToExistingMembers: false,
  };
}

function emptyDraft(): HostedPlanVersionInput {
  return {
    title: '',
    status: 'active',
    displayOrder: 10,
    monthlyPriceIdr: 0,
    annualPriceIdr: 0,
    r2DeliveryQuotaBytes: 0,
    protectedArchiveQuotaBytes: 0,
    processingAllowanceSeconds: 0,
    subtitleAllowanceSeconds: 0,
    deletedVideoRecoveryDays: 7,
    sourceRecommendedBytes: 2_000_000_000,
    sourceMaxBytes: 5_000_000_000,
    sourceMaxDurationSeconds: 10_800,
    features: { ...FEATURE_DEFAULTS, ...PUBLIC_BRANDING_DEFAULTS },
    reason: '',
    applyImprovementsToExistingMembers: false,
  };
}

interface PlanEditor {
  version: number;
  draft: HostedPlanVersionInput;
  preview: HostedPlanPublicationPreview | null;
  error: string;
  errorAction?: 'preview' | 'publish';
}

function editorFor(plan?: HostedPlan): PlanEditor {
  return { version: plan?.version ?? 0, draft: plan ? draftFor(plan) : emptyDraft(), preview: null, error: '' };
}

export function HostedPlansManager({ plans, loading = false, reload, notify, runWithRecentAuth }: Props) {
  const [selectedCode, setSelectedCode] = useState('');
  const selected = plans.find((plan) => plan.code === selectedCode);
  const [creating, setCreating] = useState(false);
  const [editors, setEditors] = useState<Record<string, PlanEditor>>({});
  const [newEditor, setNewEditor] = useState(() => editorFor());
  const initialEditor = useMemo(() => selected ? editorFor(selected) : null, [selected]);
  // A changed catalog version invalidates only that plan's old draft and preview.
  const cachedEditor = selected ? editors[selected.code] : undefined;
  const editor = creating ? newEditor : cachedEditor?.version === selected?.version ? cachedEditor : initialEditor;
  const draft = editor?.draft;
  const preview = editor?.preview;
  const error = editor?.error;
  const [busy, setBusy] = useState('');

  function changeEditor(change: (current: PlanEditor) => PlanEditor) {
    if (creating) setNewEditor(change);
    else if (selected) setEditors((current) => ({
      ...current,
      [selected.code]: change(current[selected.code]?.version === selected.version ? current[selected.code]! : editorFor(selected)),
    }));
  }
  const setPreview = (value: HostedPlanPublicationPreview | null) => changeEditor((current) => ({ ...current, preview: value }));
  const setError = (value: string, action: 'preview' | 'publish' = 'preview') => changeEditor((current) => ({ ...current, error: value, errorAction: action }));

  const update = <K extends keyof HostedPlanVersionInput>(key: K, value: HostedPlanVersionInput[K]) => {
    changeEditor((current) => ({ ...current, draft: { ...current.draft, [key]: value }, preview: null, error: '' }));
  };
  const numeric = (key: keyof HostedPlanVersionInput, value: string, multiplier = 1) => update(key, Math.round(Number(value) * multiplier) as never);
  const updateStatus = (status: HostedPlanVersionInput['status']) => {
    changeEditor((current) => ({ ...current, preview: null, error: '', draft: {
      ...current.draft, status,
      ...(status === 'active' ? {} : { applyImprovementsToExistingMembers: false }),
    } }));
  };
  const feature = (key: string, value: boolean | number | string) => {
    changeEditor((current) => ({ ...current, draft: { ...current.draft, features: { ...current.draft.features, [key]: value } }, preview: null, error: '' }));
  };

  async function previewVersion(event: FormEvent) {
    event.preventDefault();
    if (busy || !draft || (!creating && !selected)) return;
    const keyLimit = draft.features['integration.api_key_limit'];
    const shareLimit = draft.features['public_share.link_limit'];
    if ((keyLimit === undefined) !== (shareLimit === undefined)) {
      setError('Set both the API-key and public-link limits, or leave both blank for a legacy-compatible plan.');
      return;
    }
    setBusy('preview'); setError('');
    try {
      if (creating) {
        const { reason: _reason, applyImprovementsToExistingMembers: _rollout, ...initialInput } = draft;
        setPreview(await api.previewHostedPlan(initialInput));
      } else {
        setPreview(await api.previewHostedPlanVersion(selected!.code, {
          ...draft,
          reason: `Owner published a new ${draft.title} plan version from the plan catalog.`,
        }));
      }
    } catch (reason) { setError(message(reason)); } finally { setBusy(''); }
  }

  async function publishVersion() {
    if (busy || !preview || !draft || (!creating && !selected)) return;
    setBusy('publish'); setError('');
    try {
      await runWithRecentAuth(creating ? 'Create plan' : 'Publish plan version', async () => {
        let result: HostedPlanPublicationPreview;
        if (creating) {
          const { reason: _reason, applyImprovementsToExistingMembers: _rollout, ...initialInput } = draft;
          result = await api.createHostedPlan({ ...initialInput, planCode: preview.planCode });
        } else {
          result = await api.publishHostedPlanVersion(selected!.code, {
            ...draft,
            reason: `Owner published a new ${draft.title} plan version from the plan catalog.`,
          });
        }
        notify(creating
          ? `${draft.title} created.`
          : result.existingMemberRollout.appliedMemberCount > 0
            ? `${selected!.title} version ${result.nextVersion} published and applied to ${result.existingMemberRollout.appliedMemberCount} existing Member${result.existingMemberRollout.appliedMemberCount === 1 ? '' : 's'}.`
            : `${selected!.title} version ${result.nextVersion} published.`);
        setPreview(null);
        await reload();
        setEditors((current) => {
          const next = { ...current };
          delete next[result.planCode];
          return next;
        });
        if (creating) setNewEditor(editorFor());
        setSelectedCode(result.planCode);
        setCreating(false);
      });
    } catch (reason) { setError(message(reason), 'publish'); } finally { setBusy(''); }
  }

  function beginCreate() {
    if (busy || loading) return;
    setCreating(true);
    setSelectedCode('');
  }

  function cancelCreate() {
    if (busy) return;
    setCreating(false);
    setSelectedCode('');
    setNewEditor(editorFor());
  }

  const contents = draft && (creating || selected) && <>
    <form className="hosted-plan-editor dialog-form" onSubmit={previewVersion}>
      <div className="hosted-plan-editor__heading"><p>{creating ? 'The internal code is generated from the title and remains stable.' : `Publish as v${(selected?.version ?? 0) + 1}. Existing Members keep their current version unless you apply improvements below.`}</p>{creating ? <button className="button button--ghost" type="button" onClick={cancelCreate} disabled={!!busy}>Cancel</button> : <span>{selected?.affectedFutureRenewals} future renewal{selected?.affectedFutureRenewals === 1 ? '' : 's'}</span>}</div>
      <fieldset className="form-section" disabled={!!busy}><legend>Plan details</legend><div className="form-section__grid form-section__grid--three">
        <label>Plan title<input value={draft.title} minLength={2} maxLength={120} onChange={(event) => update('title', event.target.value)} required /></label>
        <label>Status<select className="control-select" value={draft.status} onChange={(event) => updateStatus(event.target.value as HostedPlanVersionInput['status'])}><option value="draft">Draft</option><option value="active">Active</option><option value="retired">Retired</option></select></label>
        <label>Display order<input type="number" min={0} max={100000} value={draft.displayOrder} onChange={(event) => numeric('displayOrder', event.target.value)} required /></label>
      </div><div className="form-section__grid">
        <label>Monthly price (IDR)<input type="number" min={0} value={draft.monthlyPriceIdr} onChange={(event) => numeric('monthlyPriceIdr', event.target.value)} required /></label>
        <label>Annual price (IDR)<input type="number" min={0} value={draft.annualPriceIdr} onChange={(event) => numeric('annualPriceIdr', event.target.value)} required /></label>
      </div></fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Included quotas</legend><div className="form-section__grid">
        <label>R2 delivery (GB)<input aria-label="R2 delivery (GB)" type="number" min={1} max={5000} value={draft.r2DeliveryQuotaBytes / 1_000_000_000} onChange={(event) => numeric('r2DeliveryQuotaBytes', event.target.value, 1_000_000_000)} required /><small>Storage add-ons increase this quota.</small></label>
        <label>Protected archive (GB)<input aria-label="Protected archive (GB)" type="number" min={0} max={2500} value={draft.protectedArchiveQuotaBytes / 1_000_000_000} onChange={(event) => numeric('protectedArchiveQuotaBytes', event.target.value, 1_000_000_000)} required /><small>Base-plan quota. Storage add-ons do not increase it.</small></label>
        <label>Processing / month (minutes)<input type="number" min={1} value={draft.processingAllowanceSeconds / 60} onChange={(event) => numeric('processingAllowanceSeconds', event.target.value, 60)} required /></label>
        <label>AI subtitles / month (minutes)<input type="number" min={1} value={draft.subtitleAllowanceSeconds / 60} onChange={(event) => numeric('subtitleAllowanceSeconds', event.target.value, 60)} required /></label>
      </div></fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Integration &amp; public sharing</legend><p className="form-help">Set both limits to enable Member API keys and public share links for this plan. Leave both blank only for a custom or legacy-compatible plan with no quota policy.</p><div className="form-section__grid">
        <label>Active API keys / Member<input type="number" min={1} max={100} value={typeof draft.features['integration.api_key_limit'] === 'number' ? draft.features['integration.api_key_limit'] : ''} onChange={(event) => {
          const next = event.target.value;
          changeEditor((current) => {
            const features = { ...current.draft.features };
            if (next === '') delete features['integration.api_key_limit']; else features['integration.api_key_limit'] = Number(next);
            return { ...current, draft: { ...current.draft, features }, preview: null, error: '' };
          });
        }} /><small>1–100 active keys. Revoked keys free a slot.</small></label>
        <label>Active public links / Member<input type="number" min={1} max={10000} value={typeof draft.features['public_share.link_limit'] === 'number' ? draft.features['public_share.link_limit'] : ''} onChange={(event) => {
          const next = event.target.value;
          changeEditor((current) => {
            const features = { ...current.draft.features };
            if (next === '') delete features['public_share.link_limit']; else features['public_share.link_limit'] = Number(next);
            return { ...current, draft: { ...current.draft, features }, preview: null, error: '' };
          });
        }} /><small>1–10,000 active links across all videos. Revoked and expired links free a slot.</small></label>
      </div></fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Member public branding</legend><p className="form-help">Applies only to Member public pages and eligible embed players, never to dashboard, login, or account email branding. Existing plan versions stay unchanged until this version is published and applied.</p><div className="choice-list">
        {Object.entries(PUBLIC_BRANDING_FEATURE_LABELS).map(([key, label]) => <label key={key} className="checkbox-field"><input className="control-choice" type="checkbox" checked={draft.features[key] === true} onChange={(event) => feature(key, event.target.checked)} /><span>{label}{draft.features[key] === undefined && <small>Not set on this legacy plan. Leave untouched to preserve compatibility.</small>}</span></label>)}
      </div><div className="form-section__grid"><label>Active custom public domains / Member<select aria-label="Active custom public domains / Member" className="control-select" value={draft.features['branding.custom_domain_limit'] === undefined ? '' : String(draft.features['branding.custom_domain_limit'])} onChange={(event) => {
        const next = event.target.value;
        changeEditor((current) => {
          const features = { ...current.draft.features };
          if (next === '') delete features['branding.custom_domain_limit']; else features['branding.custom_domain_limit'] = Number(next);
          return { ...current, draft: { ...current.draft, features }, preview: null, error: '' };
        });
      }}><option value="">Not set (legacy-compatible)</option><option value="0">0 — not included</option><option value="1">1 — one active domain</option></select><small>Public share pages and embeds only. DNS, proxy, and HTTPS are configured manually before activation.</small></label></div></fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Upload & recovery limits</legend><div className="form-section__grid">
        <label>Deleted-video recovery (days)<input type="number" min={1} max={90} value={draft.deletedVideoRecoveryDays} onChange={(event) => numeric('deletedVideoRecoveryDays', event.target.value)} required /></label>
        <label>Recommended source (GB)<input type="number" min={1} max={5} value={draft.sourceRecommendedBytes / 1_000_000_000} onChange={(event) => numeric('sourceRecommendedBytes', event.target.value, 1_000_000_000)} required /></label>
        <label>Maximum source (GB)<input type="number" min={1} max={5} value={draft.sourceMaxBytes / 1_000_000_000} onChange={(event) => numeric('sourceMaxBytes', event.target.value, 1_000_000_000)} required /></label>
        <label>Maximum source duration (minutes)<input type="number" min={1} max={180} value={draft.sourceMaxDurationSeconds / 60} onChange={(event) => numeric('sourceMaxDurationSeconds', event.target.value, 60)} required /></label>
      </div></fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Features</legend><div className="choice-list">
        {Object.entries(FEATURE_LABELS).map(([key, label]) => <label key={key} className="checkbox-field hosted-plan-feature-option"><input className="control-choice" type="checkbox" checked={draft.features[key] === true} onChange={(event) => feature(key, event.target.checked)} /><span>{label}</span></label>)}
      </div><div className="form-section__grid">
        <label>Watermark capability<select className="control-select" aria-label="Watermark capability" value={String(draft.features['watermark.mode'])} onChange={(event) => feature('watermark.mode', event.target.value)}><option value="standard_static">Static only</option><option value="custom_moving">Static + moving</option><option value="full_customization">Custom text, movement &amp; position</option></select><small>Always optional; limits the available controls.</small></label>
        <label>Embed origins / video<input type="number" min={1} max={100} value={Number(draft.features['embed.origin_limit'])} onChange={(event) => feature('embed.origin_limit', Number(event.target.value))} /></label>
      </div>
      <div className="hosted-plan-security" aria-label="Always included security"><strong><Icon name="shield" size={16} />Always included</strong><ul>{Object.entries(SECURITY_FEATURE_LABELS).map(([key, label]) => <li key={key}>{label}</li>)}</ul></div>
      </fieldset>
      <fieldset className="form-section" disabled={!!busy}><legend>Publication</legend>
      {!creating && <label className="checkbox-field hosted-plan-rollout-option">
        <input className="control-choice" type="checkbox" checked={draft.applyImprovementsToExistingMembers === true} disabled={draft.status !== 'active'} onChange={(event) => update('applyImprovementsToExistingMembers', event.target.checked)} />
        <span><strong>Apply plan improvements to existing Members now</strong><small>Available for Active versions only. Updates only safe quota and feature increases. Billing periods, renewal dates, usage, add-ons, and Member data stay unchanged.</small></span>
      </label>}
      {error && editor?.errorAction !== 'publish' && <p className="form-alert" role="alert">{error}</p>}
      <div className="hosted-plan-editor__footer"><small>Changes and the active Owner are recorded automatically.</small><button className="button button--secondary" type="submit" disabled={!!busy || draft.title.trim().length < 2}>{busy === 'preview' ? 'Checking…' : creating ? 'Preview plan' : 'Preview publication'}</button></div>
      </fieldset>
    </form>

    {preview && <section className="hosted-plan-preview" aria-label="Publication preview">
      <h3>{creating ? `Create ${draft.title}` : `Publish v${preview.nextVersion}`}</h3>
      <p>{creating ? `Internal code ${preview.planCode} will identify this plan permanently.` : `${preview.affectedFutureRenewals} future renewal${preview.affectedFutureRenewals === 1 ? '' : 's'} will use the new catalog version. Existing subscriptions keep their billing period, renewal date, usage, add-ons, and data.`}</p>
      {!creating && preview.existingMemberRollout.requested && <div className={`hosted-plan-rollout-summary ${preview.existingMemberRollout.eligible ? 'is-eligible' : 'is-blocked'}`} role={preview.existingMemberRollout.eligible ? 'status' : 'alert'}>
        <strong>{preview.existingMemberRollout.eligible ? `${preview.existingMemberRollout.memberCount} existing Member${preview.existingMemberRollout.memberCount === 1 ? '' : 's'} will receive the improvements immediately.` : 'These changes cannot be applied to existing Members now.'}</strong>
        {preview.existingMemberRollout.improvements.length > 0 && <span>Improvements: {preview.existingMemberRollout.improvements.join(', ')}.</span>}
        {preview.existingMemberRollout.blockers.length > 0 && <span>Keep the current snapshot until renewal because: {preview.existingMemberRollout.blockers.join('; ')}.</span>}
      </div>}
      <ul>{preview.mappings.map((mapping) => <li key={mapping.id}>{mapping.provider} product {mapping.externalProductId} ({mapping.billingInterval}) — {mapping.action.replace('_', ' ')}</li>)}</ul>
      {error && editor?.errorAction === 'publish' && <p className="form-alert" role="alert">{error}</p>}
      <button className="button button--primary" type="button" disabled={!!busy || (preview.existingMemberRollout.requested && !preview.existingMemberRollout.eligible)} onClick={() => void publishVersion()}>{busy === 'publish' ? (creating ? 'Creating…' : 'Publishing…') : creating ? 'Create plan' : `Publish v${preview.nextVersion}`}</button>
    </section>}

    {!creating && selected && <div className="hosted-plan-history-grid">
      <section><h3>Sejoli product mappings</h3>{selected.productMappings.length ? <ul>{selected.productMappings.map((mapping) => <li key={mapping.id}><strong>{mapping.externalProductId}</strong> · {mapping.billingInterval} · v{selected.versions.find((version) => version.id === mapping.planVersionId)?.version ?? '?'} · {mapping.state}</li>)}</ul> : <p>No product mapping.</p>}</section>
      <section><h3>Version history</h3><ul>{selected.versions.map((version) => <li key={version.id}><strong>v{version.version}</strong> · {rupiah(version.prices.monthly)} / month · {formatQuotaBytes(version.r2DeliveryQuotaBytes)} R2 · {formatQuotaBytes(version.protectedArchiveQuotaBytes)} protected · {formatDate(version.publishedAt)}</li>)}</ul></section>
    </div>}
  </>;

  return <div className="hosted-plan-manager">
    <div className="hosted-plan-manager__toolbar">
      <div><h2>Plan catalog</h2><p>Create plans and publish new versions without changing existing subscriptions.</p></div>
      <button className="button button--primary" type="button" onClick={beginCreate} disabled={creating || !!busy || loading}>Add plan</button>
    </div>
    <aside className="hosted-plan-license-note" aria-label="Member plan license boundary">
      <strong>One plan covers one content owner</strong>
      <span>Each Member plan belongs to one creator, company, or organization and its own course sites. An assistant or editor may use the second dashboard session; unrelated client libraries and independently owned brands need separate Member accounts. Learner playback is not a dashboard session.</span>
    </aside>
    <div className="accordion-list" aria-label="Plan catalog entries" aria-busy={!!busy || loading}>
      {creating && <AccordionItem title="New plan" open disabled={!!busy} onToggle={cancelCreate}>{contents}</AccordionItem>}
      {plans.map((plan) => {
        const saved = editors[plan.code];
        const dirty = saved?.version === plan.version && JSON.stringify(saved.draft) !== JSON.stringify(draftFor(plan));
        return <AccordionItem key={plan.code} title={plan.title} label={`${plan.title} plan`}
          description={`${plan.code} · v${plan.version} · ${plan.status}`} open={!creating && selectedCode === plan.code}
          disabled={!!busy || loading} onToggle={() => { setCreating(false); setSelectedCode(selectedCode === plan.code ? '' : plan.code); }}
          badge={<>{dirty && <small className="accordion-item__draft">Unsaved changes</small>}<span className={`status-badge status-badge--${plan.status === 'active' ? 'ready' : plan.status === 'retired' ? 'failed' : 'processing'}`}><span className="status-badge__dot" />{plan.status}</span></>}>
          {!creating && selectedCode === plan.code && contents}
        </AccordionItem>;
      })}
      {!plans.length && !creating && <p className="workspace-empty">{loading ? 'Loading plans…' : <><strong>No plans yet</strong><br />Add the first plan, then map its monthly or annual offer from Sejoli.</>}</p>}
    </div>
  </div>;
}
